/**
 * The 'notification' namespace allows user to register/deregister for/from push notifications as well as
 * enabling/disabling the processing of websocket notifications.
 *
 * @public
 * @requires externalNotifications
 * @namespace notification
 */

import * as actions from './actions'
import { platforms } from '../../constants'

import { defer } from '../../common/utils'

import { logManager, API_LOG_TAG } from '../../logs'

const log = logManager.getLogger('NOTIFICATIONS')

/**
 * Notifications API.
 * @method api
 * @param {Function} dispatch The redux store's dispatch function.
 * @return {Object} api  The notifications API object.
 */
export default function api ({ dispatch }) {
  const notificationApi = {
    /**
     * Provides an external notification to the system for processing.
     *
     * @public
     * @requires externalNotifications
     * @memberof notification
     * @method process
     * @param {Object} notification
     * @param {string} [channel] - The channel that the notification came from.
     */
    process (notification, channel, platform = platforms.LINK) {
      log.debug(API_LOG_TAG + 'notification.process: ', notification, channel, platform)
      dispatch(actions.externalNotification(notification, channel, platform))
    },

    /**
     * Registers with Apple push notification service. Once registration is successful, the application will be able to receive
     * standard and/or voip push notifications. It can then send these notifications to the SDK with {@link api.notifications.process}
     * in order for the SDK to process them.
     *
     * @public
     * @requires push
     * @memberof notification
     * @method registerApplePush
     * @param {Object} params
     * @param {string[]} params.services - Array of services for which we wish to receive notifications.
     * @param {string} params.voipDeviceToken - The voip device token used for voip push on iOS.
     *                                          This token is required if registering for call service notifications on iOS.
     * @param {string} params.standardDeviceToken - The standardDevice token used for standard push on iOS .
     *                                              This token is required when registering for non-call service notifications.
     * @param {string} params.bundleId - The bundleId to identify the application receiving the push notification.
     * @param {string} params.clientCorrelator - Unique identifier for a client device.
     * @param {string} params.realm - The realm used by the push registration service to identify and
     *                                establish a connection with the service gateway.
     * @param {boolean} params.isProduction - If true, push notification will be sent to production.
     *                                        If false, push notification will be sent to sandbox.
     * @return {Promise} When successful,  the information of the registration.
     *                   Promise will reject with error object otherwise.
     */
    registerApplePush ({
      services,
      voipDeviceToken,
      standardDeviceToken,
      bundleId,
      clientCorrelator,
      realm,
      isProduction
    }) {
      const deferredResult = defer()
      log.debug(API_LOG_TAG + 'notification.registerApplePush', arguments)

      dispatch(
        actions.registerApplePushNotification(
          {
            services,
            voipDeviceToken,
            standardDeviceToken,
            bundleId,
            clientCorrelator,
            realm,
            isProduction
          },
          deferredResult
        )
      )

      return deferredResult.promise
    },

    /**
     * Registers with Google push notification service. Once registration is successful, the application will be able to receive
     * standard and/or voip push notifications. It can then send these notifications to the SDK with {@link api.notifications.process}
     * in order for the SDK to process them.
     *
     * @public
     * @requires push
     * @memberof notification
     * @method registerAndroidPush
     * @param {Object} params
     * @param {string[]} params.services - Array of services to register for.
     * @param {string} params.deviceToken - The device token used for standard push on Android. This token is required
     *                                      when registering for all related services notifications.
     * @param {string} params.bundleId - The bundleId to identify the application receiving the push notification.
     * @param {string} params.clientCorrelator - Unique identifier for a client device.
     * @param {string} params.realm - The realm used by the push registration service to identify
     *                                and establish a connection with the service gateway.
     * @return {Promise} When successful,  the information of the registration.
     *                   Promise will reject with error object otherwise.
     */
    registerAndroidPush ({ services, deviceToken, bundleId, clientCorrelator, realm }) {
      const deferredResult = defer()
      log.debug(API_LOG_TAG + 'notification.registerAndroidPush', arguments)

      dispatch(
        actions.registerAndroidPushNotification(
          {
            services,
            deviceToken,
            bundleId,
            clientCorrelator,
            realm
          },
          deferredResult
        )
      )
      return deferredResult.promise
    },

    /**
     * Unregister Apple push notifications.
     *
     * @public
     * @requires push
     * @memberof notification
     * @method unregisterApplePush
     * @param {string} registrationInfo - The data returned from the push registration
     * @return {Promise} When successful, the promise will resolve with undefined.
     *                   Promise will reject with error object otherwise.
     */
    unregisterApplePush (registrationInfo) {
      const deferredResult = defer()
      log.debug(API_LOG_TAG + 'notification.unregisterPush')
      dispatch(actions.unregisterApplePushNotification(registrationInfo, deferredResult))

      return deferredResult.promise
    },

    /**
     * Unregister Android push notifications.
     *
     * @public
     * @requires push
     * @memberof notification
     * @method unregisterAndroidPush
     * @param {string} registrationInfo - The data returned from the push registration
     * @return {Promise} When successful, the promise will resolve with undefined.
     *                   Promise will reject with error object otherwise.
     */
    unregisterAndroidPush (registrationInfo) {
      const deferredResult = defer()
      log.debug(API_LOG_TAG + 'notification.unregisterPush')
      dispatch(actions.unregisterAndroidPushNotification(registrationInfo, deferredResult))

      return deferredResult.promise
    },

    /**
     * Enables, or disables, the processing of websocket notifications.
     *
     * @public
     * @requires push
     * @memberof notification
     * @method enableWebsocket
     * @param {boolean} enable - Whether the websocket channel should be enabled.
     */
    enableWebsocket (enable) {
      log.debug(API_LOG_TAG + 'notification.enableWebsocket: ', enable)
      dispatch(
        actions.enableNotificationChannel('WEBSOCKET', {
          channelEnabled: enable
        })
      )
    }
  }

  return { notification: notificationApi }
}
